import React, { useState, useEffect, useMemo, useRef } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditorProvider,
  MenuButtonTextColor,
  MenuSelectTextAlign,
  RichTextField,
  MenuSelectFontFamily,
  MenuSelectFontSize,
} from "mui-tiptap";
import {
  Switch,
  FormControlLabel,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { OpenInFullIcon, ZoomInIcon, ZoomOutIcon } from "../../../icons";
import { debounce } from "lodash";
import { useDebounce } from "./hooks/useDebounce";
import { TextStyle } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { Node, Extension } from "@tiptap/core";

// Custom FontSize extension since TipTap doesn't have a built-in one
const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) =>
              element.style.fontSize?.replace(/['"]+/g, ""),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

const INPUT_DEBOUNCE_DELAY = 500;
const OUTPUT_DEBOUNCE_DELAY = 750;

const ZOOM_STORAGE_KEY = "documentEditorZoom";
const DEFAULT_ZOOM = 1.15;
const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.7;
const MAX_ZOOM = 2.0;

const CustomDiv = Node.create({
  name: "div",
  group: "block",
  content: "block*",

  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => {
          if (!attributes.class) {
            return {};
          }
          return { class: attributes.class };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, 0];
  },
});

type DocumentEditorProps = {
  renderedContent: string;
  templateContent: string;
  opened: boolean;
  initialMode?: "readonly" | "edit";
  onTemplateChange?: (newTemplate: string) => void;
  onOpenInWindow?: () => void;
  hidden?: boolean;
};

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  renderedContent,
  templateContent,
  initialMode = "readonly",
  opened,
  onOpenInWindow,
  onTemplateChange,
  hidden = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(initialMode === "edit");
  const [zoom, setZoom] = useState(() => {
    const saved = sessionStorage.getItem(ZOOM_STORAGE_KEY);
    return saved ? parseFloat(saved) : DEFAULT_ZOOM;
  });
  const styleIdRef = useRef<string>(`tiptap-styles-${Date.now()}`);
  const injectedStyleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    sessionStorage.setItem(ZOOM_STORAGE_KEY, zoom.toString());
  }, [zoom]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));

  const debouncedRenderedContent = useDebounce(
    renderedContent,
    INPUT_DEBOUNCE_DELAY
  );

  const extractAndInjectStyles = (htmlContent: string) => {
    if (injectedStyleRef.current) {
      injectedStyleRef.current.remove();
      injectedStyleRef.current = null;
    }

    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    const matches = htmlContent.match(styleRegex);

    if (matches && matches.length > 0) {
      const styleElement = document.createElement("style");
      styleElement.id = styleIdRef.current;

      let combinedStyles = "";
      matches.forEach((styleTag) => {
        const styleContent = styleTag.replace(/<\/?style[^>]*>/gi, "");
        combinedStyles += `
            #${styleIdRef.current} ${styleContent}
          `;
      });

      styleElement.textContent = combinedStyles;
      document.head.appendChild(styleElement);
      injectedStyleRef.current = styleElement;
    }
  };

  useEffect(() => {
    const contentToProcess = isEditMode ? templateContent : renderedContent;
    extractAndInjectStyles(contentToProcess);

    return () => {
      if (injectedStyleRef.current) {
        injectedStyleRef.current.remove();
        injectedStyleRef.current = null;
      }
    };
  }, [isEditMode, templateContent, renderedContent]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      CustomDiv,
      TextStyle,
      Color.configure({}),
      FontFamily.configure({
        types: ["textStyle"],
      }),
      FontSize.configure({
        types: ["textStyle"],
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "div"],
        alignments: ["left", "center", "right", "justify"],
      }),
    ],
    editable: isEditMode,
    content: isEditMode ? templateContent : renderedContent,
    editorProps: {
      attributes: {
        id: styleIdRef.current,
      },
    },
  });

  const handleTemplateChange = useMemo(() => {
    const updateFunction = (newContent: string) => {
      if (onTemplateChange) {
        onTemplateChange(newContent);
      }
    };
    return debounce(updateFunction, OUTPUT_DEBOUNCE_DELAY);
  }, [onTemplateChange]);

  // Último HTML emitido por el propio editor: cuando ese valor vuelve como
  // prop (eco vía onTemplateChange + debounce del padre), NO hay que pisar el
  // editor — setContent resetea el cursor al inicio y, si el usuario siguió
  // tipeando durante la ventana del debounce, además pierde esas teclas.
  const lastEmittedHtmlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editor) return;

    const targetContent = isEditMode ? templateContent : renderedContent;
    // En edición, ignorar ecos de la propia edición y JAMÁS pisar el editor
    // mientras tiene foco (el usuario está tipeando). Los cambios externos
    // legítimos llegan con el editor sin foco (abrir el dialog, cambiar de
    // registro).
    if (
      isEditMode &&
      (targetContent === lastEmittedHtmlRef.current || editor.isFocused)
    ) {
      return;
    }
    if (targetContent !== editor.getHTML()) {
      editor.commands.setContent(targetContent, false);
    }
  }, [editor, isEditMode, templateContent, renderedContent]);

  // Foco al ENTRAR en modo edición — solo cuando cambia el modo (con
  // templateContent en las deps, cada eco del debounce re-disparaba
  // focus('start') y mandaba el cursor al primer carácter mientras se tipeaba).
  useEffect(() => {
    if (editor && isEditMode) {
      setTimeout(() => editor.commands.focus("start"), 100);
    }
  }, [editor, isEditMode]);

  useEffect(() => {
    if (
      !isEditMode &&
      editor &&
      debouncedRenderedContent !== editor.getHTML()
    ) {
      editor.commands.setContent(debouncedRenderedContent, false);
    }
  }, [editor, isEditMode, debouncedRenderedContent]);

  useEffect(() => {
    if (!editor) return;

    const editorUpdateHandler = ({
      editor: currentEditor,
    }: {
      editor: typeof editor;
    }) => {
      const currentHtml = currentEditor.getHTML();
      lastEmittedHtmlRef.current = currentHtml;
      handleTemplateChange(currentHtml);
    };

    const focusHandler = () => {};
    const blurHandler = () => {};

    editor.setOptions({ editable: isEditMode });

    if (isEditMode) {
      editor.on("update", editorUpdateHandler);
      editor.on("focus", focusHandler);
      editor.on("blur", blurHandler);
    }

    return () => {
      editor.off("update", editorUpdateHandler);
      editor.off("focus", focusHandler);
      editor.off("blur", blurHandler);
      handleTemplateChange.cancel();
    };
  }, [editor, isEditMode, handleTemplateChange]);

  if (hidden || !editor) {
    return null;
  }

  const ModeToggleControl = (
    <FormControlLabel
      control={
        <Switch
          checked={isEditMode}
          onChange={(event) => setIsEditMode(event.target.checked)}
          size="small"
        />
      }
      label="Editar"
      labelPlacement="end"
    />
  );

  const OpenInDialogControl = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginLeft: "auto",
        paddingLeft: isEditMode ? 2 : 0,
      }}
    >
      <IconButton
        size={"small"}
        color="default"
        aria-label="add to shopping cart"
        onClick={onOpenInWindow}
      >
        <OpenInFullIcon />
      </IconButton>
    </Box>
  );

  const ZoomControls = (
    <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
      <IconButton
        size="small"
        onClick={handleZoomOut}
        disabled={zoom <= MIN_ZOOM}
      >
        <ZoomOutIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="caption"
        sx={{ mx: 0.5, minWidth: 40, textAlign: "center" }}
      >
        {Math.round(zoom * 100)}%
      </Typography>
      <IconButton
        size="small"
        onClick={handleZoomIn}
        disabled={zoom >= MAX_ZOOM}
      >
        <ZoomInIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box
      sx={{
        // In modal context (opened=true) the editor takes a fixed, dialog-
        // friendly height with internal scroll. In inline contexts it
        // keeps the auto-grow behavior.
        //
        // overflow:hidden is critical: mui-tiptap's MenuBar uses
        // position:sticky top:0 by default. Without an overflow boundary
        // here the sticky degrades and "leaks" into the Dialog's scroll
        // container — the toolbar visually detaches from the bordered
        // FieldContainer and content renders both above and below the
        // rounded-corners border.
        ...(opened && {
          position: "relative",
          height: "60vh",
          overflow: "hidden",
        }),
        "& .MuiTiptap-FieldContainer-root": opened
          ? {
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }
          : {},
        "& .MuiTiptap-RichTextContent-root": {
          ...(opened && {
            flex: "1 1 0",
            minHeight: 0,
            overflow: "auto",
          }),
          // Use CSS zoom property instead of transform:scale. transform
          // doesn't change layout dimensions, so:
          //   - ProseMirror's cursor-tracking uses LAYOUT coords, but the
          //     visible content is at VISUAL coords (scaled). As the user
          //     types, the cursor visually drifts out of sync with the text.
          //   - overflow:auto/hidden on ancestors clips the LAYOUT box, but
          //     the visually-scaled content renders past the clip.
          // CSS zoom changes layout (the element actually becomes bigger
          // or smaller), so cursor tracking and overflow detection work as
          // expected. The visual output is equivalent for most content.
          zoom,
        },
        // El editable de ProseMirror mide solo lo que ocupa el contenido: un
        // click en el área vacía debajo caía FUERA del editor (sin cursor, o
        // foco al inicio). Estirándolo al alto del contenedor, ProseMirror
        // recibe el click y posiciona el cursor en la línea más cercana.
        "& .ProseMirror": {
          minHeight: "100%",
          cursor: "text",
        },
      }}
    >
      <RichTextEditorProvider editor={editor}>
        <RichTextField
          controls={
            <Box
              sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}
            >
              <MenuControlsContainer>
                {isEditMode && (
                  <>
                    <MenuSelectHeading />
                    <MenuDivider />
                    <MenuSelectFontFamily
                      options={[
                        { label: "Arial", value: "Arial, sans-serif" },
                        {
                          label: "Courier New",
                          value: "'Courier New', monospace",
                        },
                        { label: "Georgia", value: "Georgia, serif" },
                        {
                          label: "Helvetica",
                          value: "Helvetica, Arial, sans-serif",
                        },
                        {
                          label: "Times New Roman",
                          value: "'Times New Roman', serif",
                        },
                        {
                          label: "Trebuchet MS",
                          value: "'Trebuchet MS', sans-serif",
                        },
                        { label: "Verdana", value: "Verdana, sans-serif" },
                      ]}
                    />
                    <MenuSelectFontSize />
                    <MenuDivider />
                    <MenuButtonBold />
                    <MenuButtonItalic />
                    <MenuButtonTextColor />
                    <MenuDivider />
                    <MenuSelectTextAlign
                      value={
                        editor.isActive({ textAlign: "center" })
                          ? "center"
                          : editor.isActive({ textAlign: "right" })
                          ? "right"
                          : editor.isActive({ textAlign: "justify" })
                          ? "justify"
                          : "left"
                      }
                    />
                  </>
                )}
              </MenuControlsContainer>
              {ModeToggleControl}
              {ZoomControls}
              {!opened && onOpenInWindow && OpenInDialogControl}
            </Box>
          }
        />
      </RichTextEditorProvider>
    </Box>
  );
};
